import React, { useState } from 'react'
import AddAdjusterModal from '../IncidentModals/AddAdjusterModal'
import { Alert, Button, UncontrolledPopover, PopoverBody, PopoverHeader, Label, UncontrolledTooltip } from 'reactstrap'
import { useApolloClient, gql, useQuery } from '@apollo/client'
import CustomYupInputForm from '../../Material/CustomYupInputForm'
import { SubrogationClaimSchema } from '../IncidentModals/IncidentModalsSchema'
import getFormattedMetadata from "../../Functions/getFormattedMetadata"
import UpdateSubroClaimInfo from '../Shared/UpdateSubroClaimInfo'
import RepAvatar from '../../Material/RepAvatar'
import './ClaimsTab.css'

const SUBROGATION_STATUS_CHOICES = gql`
  query SubrogationStatusChoices {
    subrogationStatusChoices: __type(name: "IncidentsSubrogationClaimStatusChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const SUBROGATION_TPA_STATUS_CHOICES = gql`
  query SubrogationTpaStatusChoices {
    subrogationTpaStatusChoices: __type(name: "IncidentsSubrogationClaimTpaStatusChoices") {
      enumValues {
        name
        description
      }
    }
  }
`

const UPDATE_SUBROGATION_CLAIM = gql`
  mutation updateSubrogationClaim($input: UpdateSubrogationClaimMutationInput!) {
    updateSubrogationClaim(input: $input) {
      ok
      errors {
        field
        messages
      }
    }
  }
`

const ALL_TPAS = gql`
  query AllTpas($incidentId: ID!) {
    allTpas (incidentId: $incidentId, claimType: "SUBROGATION") {
      edges {
        node {
          id
          name
          tpaadjusterSet {
            edges {
              node {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`

const ALL_INCIDENT_PARTIES = gql`
  query AllIncidentParties($incidentId: ID!) {
    allIncidentParties(incidentId: $incidentId) {
      edges {
        node {
          id
          partyName
          isDefault
        }
      }
    }
  }
`
export const formatValue = (value, action) => {
    if (value === null || value === undefined || value === "") return value
    const number = Number(value)
    if (Number.isNaN(number)) return value

    const formatted = Number.isInteger(number) ? number.toString() : number.toFixed(2).replace(/\.?0+$/, "")
    switch (action) {
        case "cur":
            return `$${formatted}`

        case "per":
            return `${formatted}%`

        case "num":
        default:
            return formatted
    }
}

const NULLABLE_CLAIM_FIELDS = ["paymentDate", "tpaStatus"]

const cx = (...classes) => classes.filter(Boolean).join(' ')
const Section = ({ number, title, icon, children, className = '' }) => (
    <section className={cx('subro-claim-info__section', className)}>
        <div className="subro-claim-info__section-header">
            <span className="subro-claim-info__section-icon">
                <i className={`fa ${icon}`} />
            </span>
            <span>{number}. {title}</span>
        </div>
        {children}
    </section>
)

const SummaryCard = ({ label, value, icon, tone }) => (
    <div className={cx('subro-claim-info__summary-card', `subro-claim-info__summary-card--${tone}`)}>
        <div className="subro-claim-info__summary-icon">
            <i className={`fa ${icon}`} />
        </div>
        <div className="subro-claim-info__summary-copy">
            <div className="subro-claim-info__summary-label">{label}</div>
            <div className="subro-claim-info__summary-value">{value}</div>
        </div>
    </div>
)

const SubrogationClaimInfo = ({ claim, refetch, incidentId }) => {
    const apolloClient = useApolloClient()
    const { data: statusChoicesData } = useQuery(SUBROGATION_STATUS_CHOICES)
    const { data: tpaStatusChoicesData } = useQuery(SUBROGATION_TPA_STATUS_CHOICES)
    const { data: tpaData, refetch: refetchTpas } = useQuery(ALL_TPAS, {
        variables: { incidentId }
    })
    const { data: partiesData } = useQuery(ALL_INCIDENT_PARTIES, {
        variables: { incidentId }
    })
    const statusOptions = statusChoicesData?.subrogationStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []
    const tpaStatusOptions = tpaStatusChoicesData?.subrogationTpaStatusChoices?.enumValues?.map(opt => ({
        label: opt.description || opt.name,
        value: opt.name
    })) || []
    const tpaOptions = tpaData?.allTpas?.edges?.map(edge => ({
        label: edge.node.name,
        value: edge.node.id,
        adjusters: edge.node.tpaadjusterSet.edges.map(adjusterEdge => ({
            label: adjusterEdge.node.name,
            value: adjusterEdge.node.id,
        })),
    })) || []
    const selectedTpaOption = claim?.tpa?.id ? { label: claim.tpa.name || "Current TPA", value: claim.tpa.id } : null
    const partyOptions = partiesData?.allIncidentParties?.edges?.map(edge => edge.node)?.filter(party => !party.isDefault)?.map(party => ({
        label: party.partyName || "Unnamed Party",
        value: party.id,
    })) || []

    const [editField, setEditField] = useState({ claimId: null, field: null })
    const [inputValues, setInputValues] = useState({})
    const [fieldLoading, setFieldLoading] = useState({})
    const [originalValues, setOriginalValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [error, setError] = useState(null)
    const [openAddAdjusterModal, setOpenAddAdjusterModal] = useState(false)
    const [openAssignedToModal, setOpenAssignedToModal] = useState(false)
    const dateValidationValues = {
        filedDate: inputValues?.filedDate ?? claim?.filedDate ?? null,
        acknowledgmentDate: inputValues?.acknowledgmentDate ?? claim?.acknowledgmentDate ?? null,
        paymentDate: inputValues?.paymentDate ?? claim?.paymentDate ?? null,
    }
    const hasPositiveValue = (value) => Number(value) > 0

    const getRecoveryLockReason = (estimateLabel, recoveryLabel) => (
        `Locked until ${estimateLabel} or ${recoveryLabel} is greater than 0.`
    )
    const estimatedPd = claim?.estimatedPd
    const estimatedLou = claim?.estimatedLou
    const estimatedDv = claim?.estimatedDv
    const estimatedTs = claim?.estimatedTs
    const recoveryFieldRules = {
        recoveredPd: {
            isEditable: hasPositiveValue(estimatedPd) || hasPositiveValue(claim?.recoveredPd),
            lockReason: getRecoveryLockReason("Estimated Property Damage", "Recovered Property Damage"),
        },
        recoveredLou: {
            isEditable: hasPositiveValue(estimatedLou) || hasPositiveValue(claim?.recoveredLou),
            lockReason: getRecoveryLockReason("Estimated Loss Of Use", "Loss Of Use Recovered"),
        },
        recoveredDv: {
            isEditable: hasPositiveValue(estimatedDv) || hasPositiveValue(claim?.recoveredDv),
            lockReason: getRecoveryLockReason("Estimated Diminished Value", "Recovered Diminished Value"),
        },
        recoveredTs: {
            isEditable: hasPositiveValue(estimatedTs) || hasPositiveValue(claim?.recoveredTs),
            lockReason: getRecoveryLockReason("Estimated Tow & Storage", "Recovered Tow & Storage"),
        },
    }
    const assignedToEdges = claim?.assignedTo?.edges || []
    const claimDomId = String(claim?.pk || claim?.id || 'unknown').replace(/[^A-Za-z0-9_-]/g, '')

    const editFieldHandler = (fieldName, fieldValue = "", claimId = null) => {
        const hasActiveEdit = editField?.field && editField?.claimId === claimId
        if (hasActiveEdit && editField.field !== fieldName) {
            return "Please complete the edit on the current field before editing another field."
        }

        setEditField({ claimId, field: fieldName })
        setInputValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setOriginalValues(prev => ({ ...prev, [fieldName]: fieldValue }))
        setError(null)
        return ""
    }
    const saveFieldHandler = (subrogationClaimId, fieldName, fieldValue) => {
        if (fieldErrors[fieldName]) return
        const isNullableField = NULLABLE_CLAIM_FIELDS.includes(fieldName)
        if (!isNullableField && (fieldValue === undefined || fieldValue === null || fieldValue === "")) {
            cancelEditHandler()
            return
        }
        updateSubrogationClaimHandler(subrogationClaimId, fieldName, isNullableField && !fieldValue ? null : fieldValue)
    }
    const updateInputHandler = (e) => {
        const rawValue = e.target.value
        const fieldName = e.target.name
        const fieldType = e.target.type
        let value = rawValue
        if (["daysDown"].includes(fieldName)) {
            value = rawValue === '' ? '' : parseInt(rawValue, 10)
            if (isNaN(value)) value = ''
        }
        setInputValues(prev => ({
            ...prev,
            [fieldName]: fieldType === "select" ? rawValue === "true" ? true : rawValue === "false" ? false : rawValue : value
        }))
    }
    const cancelEditHandler = () => {
        setEditField({ claimId: null, field: null })
        setInputValues({})
        setOriginalValues({})
        setFieldErrors({})
        setError("")
        setFieldLoading({})
    }
    const updateSubrogationClaimHandler = async (subrogationClaimId, name, value) => {
        try {
            const trimmedValue = typeof value === 'string' ? value.trim() : value
            if (trimmedValue === originalValues[name]) {
                cancelEditHandler()
                return
            }
            const isClearingNullableField = NULLABLE_CLAIM_FIELDS.includes(name) && trimmedValue === null
            const isValidValue = isClearingNullableField || typeof trimmedValue === "boolean" || typeof trimmedValue === "number" || Boolean(trimmedValue)
            if (!subrogationClaimId || !name || !isValidValue) return
            setFieldLoading(prev => ({ ...prev, [name]: true }))
            setError("")
            const input = {
                claimId: subrogationClaimId,
                [name]: trimmedValue
            }
            try {
                const result = await apolloClient.mutate({
                    mutation: UPDATE_SUBROGATION_CLAIM,
                    variables: { input },
                })
                const { ok, errors } = result?.data?.updateSubrogationClaim || {}
                if (ok) {
                    setEditField({ claimId: null, field: null })
                    setError("")
                    setInputValues(prev => ({ ...prev, [name]: trimmedValue }))
                    setOriginalValues(prev => ({ ...prev, [name]: trimmedValue }))
                    refetch()
                } else {
                    const errorMessage = errors?.[0]?.messages?.toString() ||
                        "Something went wrong, Please contact admin for support!"
                    setError(errorMessage)
                }
            } catch (err) {
                setError(err.message)
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setEditField({ claimId: null, field: null })
            setFieldLoading(prev => ({ ...prev, [name]: false }))
            setFieldErrors(prev => ({ ...prev, [name]: "" }))
        }
    }

    const getAdjusterOptions = () => {
        const selectedTpa = tpaOptions.find(opt => opt.value === (inputValues?.tpaId || claim?.tpa?.id))
        return selectedTpa?.adjusters || []
    }

    const handleAddAdjuster = async (newAdjuster) => {
        refetchTpas()
        setInputValues(prev => ({
            ...prev,
            adjusterId: newAdjuster.id
        }))
        setOpenAddAdjusterModal(false)
    }

    const booleanOptions = [{ value: true, label: "Yes" }, { value: false, label: "No" }]
    const decimalInputProps = { step: "any", inputMode: "decimal" }
    let formattedMetadata = getFormattedMetadata(claim.metadata)
    let hasMetadata = formattedMetadata && formattedMetadata !== '{}' && formattedMetadata !== '[]'

    const renderAssignedToField = () => (
        <div className="subro-claim-info__field subro-claim-info__field--assigned">
            <div className="custom-yup-input">
                <div className="row">
                    <div className="col-9">
                        <Label>Assigned To</Label>
                    </div>
                    <div className="col-3 text-right custom-yup-input__actions">
                        {!claim?.isArchived ? (
                            <i
                                id={`subroClaimInfoAssignedToEdit${claimDomId}`}
                                className="fa fa-lg fa-pencil-square custom-yup-input__edit"
                                onClick={() => setOpenAssignedToModal(true)}
                                title="Edit"
                            />
                        ) : (
                            <i className="fa fa-lg fa-lock" title="Read Only" />
                        )}
                    </div>
                </div>
            </div>
            <div className="subro-claim-info__assigned-content">
                <span id={`assignedTo${claimDomId}`} className="subro-claim-info__assigned-value">
                    {assignedToEdges?.length ? (
                        <>
                            {assignedToEdges.slice(0, 1).map(e => <RepAvatar key={e.node.id} rep={e.node} />)}
                            {assignedToEdges.length > 1 ? ` + ${assignedToEdges.length - 1}` : ""}
                        </>
                    ) : (
                        <span className="subro-claim-info__empty-value">--</span>
                    )}
                </span>
            </div>
            {assignedToEdges?.length > 1 && <UncontrolledTooltip target={`assignedTo${claimDomId}`} placement="top" style={{ backgroundColor: "#f2f2f2" }}>
                {assignedToEdges.map(e => <RepAvatar key={e.node.id} rep={e.node} keepOpen />)}
            </UncontrolledTooltip>}
            {openAssignedToModal &&
                <UpdateSubroClaimInfo
                    title="Edit Assigned To"
                    name="assignedToIds"
                    open={openAssignedToModal}
                    value={assignedToEdges.map(e => e.node)}
                    target={`subroClaimInfoAssignedToEdit${claimDomId}`}
                    handleClose={() => setOpenAssignedToModal(false)}
                    claimId={claim.id}
                    refetch={refetch}
                />
            }
        </div>
    )

    const renderAdjusterField = () => (
        <div className="subro-claim-info__field subro-claim-info__field--adjuster">
            <div className="subro-claim-info__adjuster-row">
                <span id={`subrogation-adjuster-info-${claim.pk || claim.id}`} className="subro-claim-info__adjuster-input">
                    <CustomYupInputForm
                        label="Adjuster"
                        fieldName={"adjusterId"}
                        inputType="select"
                        showEdit={!claim?.isArchived}
                        options={getAdjusterOptions()}
                        isEditing={editField.claimId === claim.id && editField.field === "adjusterId"}
                        isLoading={!!fieldLoading?.adjusterId}
                        inputValue={inputValues?.["adjusterId"]}
                        defaultValue={claim?.adjuster?.id}
                        onEdit={() => editFieldHandler("adjusterId", claim.adjuster?.id, claim.id)}
                        onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                        onChange={updateInputHandler}
                        onCancel={cancelEditHandler}
                    />
                </span>
                {!claim?.isArchived && editField.claimId === claim.id && editField.field === "adjusterId" &&
                    <Button color="primary" onClick={() => setOpenAddAdjusterModal(true)} className='subro-claim-info__square-button'>
                        <i className='fa fa-plus' />
                    </Button>
                }
            </div>
            <UncontrolledPopover trigger="hover" placement="right" target={`subrogation-adjuster-info-${claim.pk || claim.id}`}>
                <PopoverHeader>Adjuster Info</PopoverHeader>
                <PopoverBody>
                    <b>Name:</b> <small>{claim?.adjuster?.name || "--"} </small><br />
                    <b>Phone:</b> <small>{claim?.adjuster?.phone || "--"} </small><br />
                    <b>Email:</b> <small>{claim?.adjuster?.email || "--"} </small><br />
                    <b>Role:</b> <small>{claim?.adjuster?.role || "--"} </small><br />
                </PopoverBody>
            </UncontrolledPopover>
            {openAddAdjusterModal && (
                <AddAdjusterModal
                    isOpen={openAddAdjusterModal}
                    toggle={() => setOpenAddAdjusterModal(false)}
                    tpaId={claim?.tpa?.id}
                    onCreate={handleAddAdjuster}
                />
            )}
        </div>
    )

    return (
        <div className="subro-claim-info">
            {error && <Alert color="danger">{error}</Alert>}

            <Section number="1" title="Claim Administration" icon="fa-clipboard">
                <div className="subro-claim-info__grid subro-claim-info__grid--4">
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="Status"
                            fieldName="status"
                            inputType="select"
                            options={statusOptions}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "status"}
                            isLoading={!!fieldLoading?.status}
                            inputValue={inputValues?.status}
                            defaultValue={claim?.status}
                            onEdit={() => editFieldHandler("status", claim?.status, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.status}
                        />
                    </div>
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="TPA Status"
                            fieldName="tpaStatus"
                            inputType="select"
                            options={tpaStatusOptions}
                            isClearable
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "tpaStatus"}
                            isLoading={!!fieldLoading?.tpaStatus}
                            inputValue={inputValues?.tpaStatus}
                            defaultValue={claim?.tpaStatus}
                            onEdit={() => editFieldHandler("tpaStatus", claim?.tpaStatus, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.tpaStatus}
                        />
                    </div>
                    <div className="subro-claim-info__field subro-claim-info__field--required">
                        <CustomYupInputForm
                            label="TPA"
                            fieldName="tpaId"
                            inputType="select"
                            options={tpaOptions}
                            selectedOption={selectedTpaOption}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "tpaId"}
                            isLoading={!!fieldLoading?.tpaId}
                            inputValue={inputValues?.tpaId}
                            defaultValue={claim?.tpa?.id}
                            onEdit={() => editFieldHandler("tpaId", claim?.tpa?.id, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.tpaId}
                        />
                    </div>
                    {renderAdjusterField()}
                    <div className="subro-claim-info__field subro-claim-info__field--required">
                        <CustomYupInputForm
                            label="Party"
                            fieldName="partyId"
                            inputType="select"
                            options={partyOptions}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "partyId"}
                            isLoading={!!fieldLoading?.partyId}
                            inputValue={inputValues?.partyId}
                            defaultValue={claim?.party?.id}
                            onEdit={() => editFieldHandler("partyId", claim?.party?.id, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.partyId}
                        />
                    </div>
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="External ID"
                            fieldName="externalId"
                            inputType="text"
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "externalId"}
                            isLoading={!!fieldLoading?.externalId}
                            inputValue={inputValues?.externalId}
                            defaultValue={claim?.externalId}
                            onEdit={() => editFieldHandler("externalId", claim?.externalId, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.externalId}
                        />
                    </div>
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="In Litigation"
                            fieldName="inLitigation"
                            inputType="select"
                            options={booleanOptions}
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "inLitigation"}
                            isLoading={!!fieldLoading?.inLitigation}
                            inputValue={inputValues?.inLitigation}
                            defaultValue={claim?.inLitigation}
                            onEdit={() => editFieldHandler("inLitigation", claim?.inLitigation, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.inLitigation}
                        />
                    </div>
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="Filed Date"
                            fieldName="filedDate"
                            inputType="date"
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "filedDate"}
                            isLoading={!!fieldLoading?.filedDate}
                            inputValue={inputValues?.filedDate}
                            defaultValue={claim?.filedDate}
                            onEdit={() => editFieldHandler("filedDate", claim?.filedDate, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema}
                            validationValues={dateValidationValues}
                        />
                    </div>
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="Acknowledgment Date"
                            fieldName="acknowledgmentDate"
                            inputType="date"
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "acknowledgmentDate"}
                            isLoading={!!fieldLoading?.acknowledgmentDate}
                            inputValue={inputValues?.acknowledgmentDate}
                            defaultValue={claim?.acknowledgmentDate}
                            onEdit={() => editFieldHandler("acknowledgmentDate", claim?.acknowledgmentDate, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema}
                            validationValues={dateValidationValues}
                        />
                    </div>
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="Payment ID"
                            fieldName="paymentId"
                            inputType="text"
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "paymentId"}
                            isLoading={!!fieldLoading?.paymentId}
                            inputValue={inputValues?.paymentId}
                            defaultValue={claim?.paymentId}
                            onEdit={() => editFieldHandler("paymentId", claim?.paymentId, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.paymentId}
                        />
                    </div>
                    <div className="subro-claim-info__field">
                        <CustomYupInputForm
                            label="Payment Date"
                            fieldName="paymentDate"
                            inputType="date"
                            showEdit={!claim?.isArchived}
                            isEditing={editField.claimId === claim.id && editField.field === "paymentDate"}
                            isLoading={!!fieldLoading?.paymentDate}
                            inputValue={inputValues?.paymentDate}
                            defaultValue={claim?.paymentDate}
                            onEdit={() => editFieldHandler("paymentDate", claim?.paymentDate, claim.id)}
                            onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                            onChange={updateInputHandler}
                            onCancel={cancelEditHandler}
                            yupSchema={SubrogationClaimSchema.fields.paymentDate}
                        />
                    </div>
                    {renderAssignedToField()}
                </div>
            </Section>

            <Section number="2" title="Recovery Summary" icon="fa-clipboard">
                <div className="subro-claim-info__summary-grid">
                    <SummaryCard label="Requested Amount" value={formatValue(claim?.requestedAmount, "cur")} icon="fa-usd" tone="blue" />
                    <SummaryCard label="Gross Collected" value={formatValue(claim?.grossCollected, "cur")} icon="fa-money" tone="green" />
                    <SummaryCard label="Net Collected" value={formatValue(claim?.netCollected, "cur")} icon="fa-bar-chart" tone="purple" />
                    <SummaryCard label="Recovery Rate" value={formatValue(claim?.recoveryRate || 0, "per")} icon="fa-pie-chart" tone="orange" />
                </div>
            </Section>

            <div className="subro-claim-info__two-column">
                <Section number="3" title="Demand Breakdown" icon="fa-clipboard">
                    <div className="subro-claim-info__grid subro-claim-info__grid--2">
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Internal Estimated Repair"
                                fieldName="estimatedRepair"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "estimatedRepair"}
                                isLoading={!!fieldLoading?.estimatedRepair}
                                inputValue={inputValues?.estimatedRepair}
                                defaultValue={claim?.estimatedRepair}
                                onEdit={() => editFieldHandler("estimatedRepair", claim?.estimatedRepair, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.estimatedRepair}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Estimated Property Damage"
                                fieldName="estimatedPd"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "estimatedPd"}
                                isLoading={!!fieldLoading?.estimatedPd}
                                inputValue={inputValues?.estimatedPd}
                                defaultValue={claim?.estimatedPd}
                                onEdit={() => editFieldHandler("estimatedPd", claim?.estimatedPd, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.estimatedPd}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Estimated Loss Of Use"
                                fieldName="estimatedLou"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "estimatedLou"}
                                isLoading={!!fieldLoading?.estimatedLou}
                                inputValue={inputValues?.estimatedLou}
                                defaultValue={claim?.estimatedLou}
                                onEdit={() => editFieldHandler("estimatedLou", claim?.estimatedLou, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.estimatedLou}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Estimated Diminished Value"
                                fieldName="estimatedDv"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "estimatedDv"}
                                isLoading={!!fieldLoading?.estimatedDv}
                                inputValue={inputValues?.estimatedDv}
                                defaultValue={claim?.estimatedDv}
                                onEdit={() => editFieldHandler("estimatedDv", claim?.estimatedDv, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.estimatedDv}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Estimated Tow & Storage"
                                fieldName="estimatedTs"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "estimatedTs"}
                                isLoading={!!fieldLoading?.estimatedTs}
                                inputValue={inputValues?.estimatedTs}
                                defaultValue={claim?.estimatedTs}
                                onEdit={() => editFieldHandler("estimatedTs", claim?.estimatedTs, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.estimatedTs}
                            />
                        </div>
                        <div className="subro-claim-info__field subro-claim-info__field--calculated subro-claim-info__field--span-2">
                            <CustomYupInputForm
                                label="Liability Percentage"
                                fieldName="liabilityPercentage"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={false}
                                defaultValue={formatValue(claim?.liabilityPercentage, "num")}
                            />
                        </div>
                        {/* <div className="subro-claim-info__field subro-claim-info__field--calculated subro-claim-info__field--span-2">
                            <CustomYupInputForm
                                label="Requested Amount"
                                fieldName="requestedAmount"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={false}
                                defaultValue={formatValue(claim?.requestedAmount, "num")}
                            />
                        </div> */}
                    </div>
                </Section>

                <Section number="4" title="Recovery Breakdown" icon="fa-file-text-o">
                    <div className="subro-claim-info__grid subro-claim-info__grid--2">
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Actual Repair"
                                fieldName="actualRepair"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "actualRepair"}
                                isLoading={!!fieldLoading?.actualRepair}
                                inputValue={inputValues?.actualRepair}
                                defaultValue={claim?.actualRepair}
                                onEdit={() => editFieldHandler("actualRepair", claim?.actualRepair, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.actualRepair}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Recovered Property Damage"
                                fieldName="recoveredPd"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived && recoveryFieldRules.recoveredPd.isEditable}
                                readOnlyTitle={claim?.isArchived ? "Claim is archived." : recoveryFieldRules.recoveredPd.lockReason}
                                isEditing={editField.claimId === claim.id && editField.field === "recoveredPd"}
                                isLoading={!!fieldLoading?.recoveredPd}
                                inputValue={inputValues?.recoveredPd}
                                defaultValue={claim?.recoveredPd}
                                onEdit={() => editFieldHandler("recoveredPd", claim?.recoveredPd, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.recoveredPd}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Recovered Loss Of Use"
                                fieldName="recoveredLou"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived && recoveryFieldRules.recoveredLou.isEditable}
                                readOnlyTitle={claim?.isArchived ? "Claim is archived." : recoveryFieldRules.recoveredLou.lockReason}
                                isEditing={editField.claimId === claim.id && editField.field === "recoveredLou"}
                                isLoading={!!fieldLoading?.recoveredLou}
                                inputValue={inputValues?.recoveredLou}
                                defaultValue={claim?.recoveredLou}
                                onEdit={() => editFieldHandler("recoveredLou", claim?.recoveredLou, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.recoveredLou}
                            />
                        </div>

                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Recovered Diminished Value"
                                fieldName="recoveredDv"
                                inputType="number"
                                inputProps={decimalInputProps}
                                defaultValue={formatValue(claim?.recoveredDv, "num")}
                                showEdit={!claim?.isArchived && recoveryFieldRules.recoveredDv.isEditable}
                                readOnlyTitle={claim?.isArchived ? "Claim is archived." : recoveryFieldRules.recoveredDv.lockReason}
                                isEditing={editField.claimId === claim.id && editField.field === "recoveredDv"}
                                isLoading={!!fieldLoading?.recoveredDv}
                                inputValue={inputValues?.recoveredDv}
                                onEdit={() => editFieldHandler("recoveredDv", claim?.recoveredDv, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.recoveredDv}
                            />
                        </div>

                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Recovered Tow & Storage"
                                fieldName="recoveredTs"
                                inputType="number"
                                inputProps={decimalInputProps}
                                defaultValue={formatValue(claim?.recoveredTs, "num")}
                                showEdit={!claim?.isArchived && recoveryFieldRules.recoveredTs.isEditable}
                                readOnlyTitle={claim?.isArchived ? "Claim is archived." : recoveryFieldRules.recoveredTs.lockReason}
                                isEditing={editField.claimId === claim.id && editField.field === "recoveredTs"}
                                isLoading={!!fieldLoading?.recoveredTs}
                                inputValue={inputValues?.recoveredTs}
                                onEdit={() => editFieldHandler("recoveredTs", claim?.recoveredTs, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.recoveredTs}
                            />
                        </div>
                        <div className="subro-claim-info__field subro-claim-info__field--calculated">
                            <CustomYupInputForm
                                label="TPA Fee"
                                fieldName="tpaFee"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={false}
                                defaultValue={formatValue(claim?.tpaFee, "num")}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="TPA Expenses"
                                fieldName="tpaExpenses"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "tpaExpenses"}
                                isLoading={!!fieldLoading?.tpaExpenses}
                                inputValue={inputValues?.tpaExpenses}
                                defaultValue={claim?.tpaExpenses}
                                onEdit={() => editFieldHandler("tpaExpenses", claim?.tpaExpenses, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.tpaExpenses}
                            />
                        </div>
                        <div className="subro-claim-info__field subro-claim-info__field--calculated">
                            <CustomYupInputForm
                                label="TPA Net Collected"
                                fieldName="tpaNetCollected"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={false}
                                defaultValue={formatValue(claim?.tpaNetCollected, "num")}
                            />
                        </div>
                        {/* <div className="subro-claim-info__field subro-claim-info__field--calculated">
                            <CustomYupInputForm
                                label="Net Collected"
                                fieldName="netCollected"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={false}
                                defaultValue={formatValue(claim?.netCollected, "num")}
                            />
                        </div> */}
                        {/* <div className="subro-claim-info__field subro-claim-info__field--calculated subro-claim-info__field--span-2">
                            <CustomYupInputForm
                                label="Recovery Rate"
                                fieldName="recoveryRate"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={false}
                                defaultValue={formatValue(claim?.recoveryRate, "num")}
                            />
                        </div> */}
                    </div>
                </Section>
            </div>

            <div className="subro-claim-info__two-column">
                <Section number="5" title="Loss Of Use" icon="fa-clock-o">
                    <div className="subro-claim-info__grid subro-claim-info__grid--3">
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Daily Rate"
                                fieldName="dailyRate"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "dailyRate"}
                                isLoading={!!fieldLoading?.dailyRate}
                                inputValue={inputValues?.dailyRate}
                                defaultValue={claim?.dailyRate}
                                onEdit={() => editFieldHandler("dailyRate", claim?.dailyRate, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.dailyRate}
                            />
                        </div>
                        <div className="subro-claim-info__field">
                            <CustomYupInputForm
                                label="Days Down"
                                fieldName="daysDown"
                                inputType="number"
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "daysDown"}
                                isLoading={!!fieldLoading?.daysDown}
                                inputValue={inputValues?.daysDown}
                                defaultValue={claim?.daysDown}
                                onEdit={() => editFieldHandler("daysDown", claim?.daysDown, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.daysDown}
                            />
                        </div>
                        <div className="subro-claim-info__field subro-claim-info__field--calculated">
                            <CustomYupInputForm
                                label="Loss Of Use"
                                fieldName="lossOfUse"
                                inputType="number"
                                inputProps={decimalInputProps}
                                showEdit={false}
                                defaultValue={claim?.lossOfUse}
                            />
                        </div>
                    </div>
                </Section>

                <Section number="6" title="Notes" icon="fa-sticky-note-o">
                    <div className="subro-claim-info__grid">
                        <div className="subro-claim-info__field subro-claim-info__field--notes">
                            <CustomYupInputForm
                                label="Notes"
                                fieldName="notes"
                                inputType="textarea"
                                showEdit={!claim?.isArchived}
                                isEditing={editField.claimId === claim.id && editField.field === "notes"}
                                isLoading={!!fieldLoading?.notes}
                                inputValue={inputValues?.notes}
                                defaultValue={claim?.notes}
                                onEdit={() => editFieldHandler("notes", claim?.notes, claim.id)}
                                onSave={(fieldName, fieldValue) => saveFieldHandler(claim.id, fieldName, fieldValue)}
                                onChange={updateInputHandler}
                                onCancel={cancelEditHandler}
                                yupSchema={SubrogationClaimSchema.fields.notes}
                            />
                        </div>
                    </div>
                </Section>
            </div>

            {hasMetadata && (
                <Section number="7" title="Metadata" icon="fa-database">
                    <pre className="subro-claim-info__metadata">
                        {formattedMetadata}
                    </pre>
                </Section>
            )}
        </div>
    )
}

export default SubrogationClaimInfo
